import {
  createContext, useCallback, useContext, useMemo, useRef, useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { CircleAlert, CircleCheck, Info } from 'lucide-react'
import { Button } from './Button'

/* ---------------------------------------------------------------- notice */

interface NoticeProps {
  icon?: ReactNode
  title: string
  body?: ReactNode
  action?: { label: string; onClick: () => void }
}

/** Empty and error states, kept to one shape so they never feel improvised. */
export function Notice({ icon, title, body, action }: NoticeProps) {
  return (
    <div className="notice">
      {icon ? <div className="notice__icon">{icon}</div> : null}
      <h2 className="notice__title">{title}</h2>
      {body ? <p className="notice__body">{body}</p> : null}
      {action ? (
        <div className="notice__action">
          <Button size="sm" variant="outline" onClick={action.onClick}>
            {action.label}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------- skeleton */

export function Skeleton({
  height = '1rem',
  width = '100%',
  style,
}: {
  height?: string
  width?: string
  style?: React.CSSProperties
}) {
  return (
    <div className="skeleton" style={{ height, width, ...style }} aria-hidden="true" />
  )
}

/* ----------------------------------------------------------------- toast */

type ToastTone = 'ok' | 'bad' | 'neutral'

interface Toast {
  id: number
  tone: ToastTone
  message: string
}

interface ToastApi {
  show: (message: string, tone?: ToastTone) => void
}

const ToastContext = createContext<ToastApi | null>(null)

const TONE_ICON: Record<ToastTone, ReactNode> = {
  ok: <CircleCheck size={18} strokeWidth={1.6} />,
  bad: <CircleAlert size={18} strokeWidth={1.6} />,
  neutral: <Info size={18} strokeWidth={1.6} />,
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const seq = useRef(0)

  const show = useCallback((message: string, tone: ToastTone = 'neutral') => {
    const id = ++seq.current
    setToasts((prev) => [...prev, { id, tone, message }])
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 3600)
  }, [])

  const value = useMemo(() => ({ show }), [show])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="toast-layer" aria-live="polite" aria-atomic="false">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`toast ${t.tone === 'neutral' ? '' : `toast--${t.tone}`}`}
              role={t.tone === 'bad' ? 'alert' : 'status'}
            >
              <span className="toast__icon" aria-hidden="true">
                {TONE_ICON[t.tone]}
              </span>
              {t.message}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}
